import {
  Alert,
  Box,
  Checkbox,
  Divider,
  FormControlLabel,
  InputBase,
  MenuItem,
  Select,
  styled,
} from "@mui/material";
import { useSnackbar } from "notistack";
import React, { useRef, useState } from "react";

import { ApiIdentityCreationResponse, CreateApiIdentityData } from "api/services/useApiIdentities";
import { AddIcon } from "assets/icons";
import { useAppDispatch, useAppSelector } from "hooks/store";
import { GroundlightButton } from "new-components/buttons/GroundlightButton";
import { Typography } from "new-components/typography/Typography";
import { getApiIdentities } from "store/services/ApiIdentities/apiIdentitiesThunks";
import { setOrdering, setPage } from "store/slices/ApiIdentities/apiIdentitiesSlice";

const CustomTextField = styled(InputBase)(() => ({
  "& .MuiInputBase-input": {
    border: "1px solid #E5E9ED",
    backgroundColor: "white",
    fontFamily: "inherit",
    fontSize: "20px",
    fontWeight: 400,
    lineHeight: "24px",
    padding: "8px 12px",
    letterSpacing: "0.5px",
    borderRadius: "4px",
  },
  "&.Mui-error": {
    border: "1px solid #d32f2f",
  },
  "&.Mui-focused": {
    border: "none",
  },
}));

/** Matches ApiIdentity.name / ApiIdentityCreateSerializer max_length. */
const MAX_NAME_LENGTH = 64;

interface CreateFormProps {
  isLoading: boolean;
  createApiIdentity: (data: CreateApiIdentityData) => Promise<ApiIdentityCreationResponse>;
  onClose: () => void;
}

function durationToSeconds(value: number, unit: string): number {
  if (unit === "seconds") return value;
  if (unit === "minutes") return value * 60;
  if (unit === "hours") return value * 60 * 60;
  return value * 24 * 60 * 60;
}

const FALLBACK_CREATE_ERROR = "Failed to create token. Please try again.";

/** Return the first non-empty string from a DRF error field (string or list). */
function firstErrorString(value: unknown): string | null {
  if (typeof value === "string" && value.trim()) return value;
  if (Array.isArray(value) && typeof value[0] === "string" && value[0].trim()) return value[0];
  return null;
}

/** Prefer the API's error body for the create toast; fall back to a generic sentence. */
function createTokenErrorMessage(error: unknown): string {
  if (error && typeof error === "object" && "data" in error) {
    const { data } = error as { data?: unknown };
    const fromData = firstErrorString(data);
    if (fromData) return fromData;
    if (data && typeof data === "object" && !Array.isArray(data)) {
      const record = data as Record<string, unknown>;
      const fromDetail = firstErrorString(record.detail);
      if (fromDetail) return fromDetail;
      const fromName = firstErrorString(record.name);
      if (fromName) return fromName;
    }
  }
  return FALLBACK_CREATE_ERROR;
}

function addDurationFromNow(value: number, unit: string): string {
  const now = new Date();
  if (unit === "seconds") {
    now.setSeconds(now.getSeconds() + value);
  } else if (unit === "minutes") {
    now.setMinutes(now.getMinutes() + value);
  } else if (unit === "hours") {
    now.setHours(now.getHours() + value);
  } else {
    now.setDate(now.getDate() + value);
  }
  return now.toISOString();
}

function DurationRow(props: {
  value: number;
  unit: string;
  disabled: boolean;
  valueAriaLabel: string;
  unitAriaLabel: string;
  onValueChange: (value: number) => void;
  onUnitChange: (unit: string) => void;
}) {
  const { value, unit, disabled, valueAriaLabel, unitAriaLabel, onValueChange, onUnitChange } = props;
  return (
    <Box
      className="flex h-[42px] gap-2 items-center"
      sx={{
        opacity: disabled ? 0.45 : 1,
        transition: "opacity 150ms ease-in-out",
      }}
    >
      <CustomTextField
        type="number"
        value={value}
        onChange={(e) => onValueChange(parseInt(e.target.value, 10) || 0)}
        className="w-24"
        inputProps={{ min: 1, "aria-label": valueAriaLabel }}
        disabled={disabled}
      />
      <Select
        value={unit}
        onChange={(e) => onUnitChange(e.target.value as string)}
        size="small"
        disabled={disabled}
        inputProps={{ "aria-label": unitAriaLabel }}
        sx={{ height: "42px", minWidth: 120 }}
      >
        <MenuItem value="seconds">seconds</MenuItem>
        <MenuItem value="minutes">minutes</MenuItem>
        <MenuItem value="hours">hours</MenuItem>
        <MenuItem value="days">days</MenuItem>
      </Select>
    </Box>
  );
}

export function CreateForm(props: CreateFormProps) {
  const { isLoading, createApiIdentity, onClose } = props;
  const { rowsPerPage, search, includeRevoked } = useAppSelector((state) => state.apiIdentities);
  const dispatch = useAppDispatch();

  const [name, setName] = useState<string>("");
  // Default Automatic rotation on (30d). Uncheck for never-expiring minted secrets.
  const [hasTokenTtl, setHasTokenTtl] = useState<boolean>(true);
  const [ttlValue, setTtlValue] = useState<number>(30);
  const [ttlUnit, setTtlUnit] = useState<string>("days");
  const [accessExpires, setAccessExpires] = useState<boolean>(false);
  const [hardExpireValue, setHardExpireValue] = useState<number>(30);
  const [hardExpireUnit, setHardExpireUnit] = useState<string>("days");

  const inputRef = useRef<HTMLInputElement | null>(null);
  const { enqueueSnackbar } = useSnackbar();

  const handleCreate = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    let tokenTtl: number | null = null;
    if (hasTokenTtl) {
      const ttlSeconds = durationToSeconds(ttlValue, ttlUnit);
      if (ttlSeconds <= 0) {
        enqueueSnackbar("Automatic rotation interval must be a positive duration", { variant: "error" });
        return;
      }
      tokenTtl = ttlSeconds;
    }

    let expiresAt: string | null = null;
    if (accessExpires) {
      const expireSeconds = durationToSeconds(hardExpireValue, hardExpireUnit);
      if (expireSeconds <= 0) {
        enqueueSnackbar("Expiration must be a positive duration", { variant: "error" });
        return;
      }
      expiresAt = addDurationFromNow(hardExpireValue, hardExpireUnit);
    }

    const payload: CreateApiIdentityData = {
      name,
      token_ttl: tokenTtl,
      expires_at: expiresAt,
    };

    try {
      await createApiIdentity(payload);
      dispatch(setOrdering("-created_at"));
      dispatch(
        getApiIdentities({
          pageSize: rowsPerPage,
          ordering: "-created_at",
          search,
          includeRevoked,
        })
      );
      dispatch(setPage(0));
    } catch (error) {
      // Automatic mutation snackbars are disabled in useCreateApiIdentity.
      enqueueSnackbar(createTokenErrorMessage(error), { variant: "error" });
    }
  };

  return (
    <form
      className="flex flex-col gap-3 pt-[15px] pb-6 w-full h-full justify-between box-border"
      onSubmit={handleCreate}
      autoComplete="off"
    >
      <Box className="flex flex-col gap-3">
        <Box className="flex flex-col gap-1">
          <Typography variant="subtitle" className="text-primary-darker sm:text-[#15121A]">
            Name
          </Typography>
          <CustomTextField
            value={name}
            onChange={(event) => setName(event.target.value.slice(0, MAX_NAME_LENGTH))}
            inputRef={inputRef}
            autoFocus
            required
            placeholder="Where will this token be used?"
            // Keep password managers from treating this as a personal-name / login field.
            name="api-token-display-name"
            autoComplete="off"
            inputProps={{
              maxLength: MAX_NAME_LENGTH,
              "aria-label": "Name",
              "data-1p-ignore": true,
              "data-lpignore": "true",
              "data-bwignore": true,
              "data-form-type": "other",
            }}
          />
          {/* Always mounted so the counter never shifts the dialog when typing starts. */}
          <Box className="flex justify-end min-h-[20px]">
            <Typography
              variant="detail"
              className="shrink-0 tabular-nums"
              sx={{
                color: name.length >= MAX_NAME_LENGTH ? "#d32f2f" : "#919599",
                visibility: name.length > 0 ? "visible" : "hidden",
              }}
            >
              {name.length}/{MAX_NAME_LENGTH}
            </Typography>
          </Box>
        </Box>

        <Box className="flex flex-col gap-2">
          <FormControlLabel
            control={<Checkbox checked={accessExpires} onChange={(e) => setAccessExpires(e.target.checked)} />}
            label={<Typography variant="subtitle">Expiration</Typography>}
          />
          <Typography variant="detail" className="text-primary-darker">
            Hard deadline for this access. After this time, tokens stop working and can no longer be refreshed.
          </Typography>
          <DurationRow
            value={hardExpireValue}
            unit={hardExpireUnit}
            disabled={!accessExpires}
            valueAriaLabel="Expiration value"
            unitAriaLabel="Expiration unit"
            onValueChange={setHardExpireValue}
            onUnitChange={setHardExpireUnit}
          />
        </Box>

        <Box className="flex flex-col gap-2">
          <FormControlLabel
            control={<Checkbox checked={hasTokenTtl} onChange={(e) => setHasTokenTtl(e.target.checked)} />}
            label={<Typography variant="subtitle">Automatic rotation</Typography>}
          />
          <Typography variant="detail" className="text-primary-darker">
            Clients automatically rotate the token before this duration elapses. This limits how long a leaked token
            remains usable. If a client is offline longer than this duration, the token expires.
          </Typography>
          <DurationRow
            value={ttlValue}
            unit={ttlUnit}
            disabled={!hasTokenTtl}
            valueAriaLabel="Automatic rotation value"
            unitAriaLabel="Automatic rotation unit"
            onValueChange={setTtlValue}
            onUnitChange={setTtlUnit}
          />
          {/* Always mounted so toggling rotation off does not resize the dialog. */}
          <Box sx={{ minHeight: 40 }}>
            {!hasTokenTtl && (
              <Alert severity="warning" sx={{ py: 0 }}>
                Disabling automatic rotation is discouraged. Only disable it if you need a fixed token.
              </Alert>
            )}
          </Box>
        </Box>
      </Box>

      <Box className="flex flex-col gap-3">
        <Divider />
        <Box className="flex gap-2.5 justify-center">
          <GroundlightButton type="submit" disabled={name.length <= 0 || isLoading} endIcon={AddIcon}>
            Create token
          </GroundlightButton>
          <GroundlightButton onClick={onClose} variant="plain" className="sm:hidden">
            Close
          </GroundlightButton>
        </Box>
      </Box>
    </form>
  );
}
